import type { AuthV1, ConfigV1, CreditV1, DocTemplateV1, DocumentUnlockSettingsV1, DocumentV1, IdentityV1, JobQueueV1, MagicLinkRequestBody, PaymentV1, UsageLogV1, VoucherV1 } from '@alpod-ai-writer/backend';

import type { ApiClient } from './apiClient';

export const createAdminApi = (client: ApiClient) => ({
    client,
    v1: {
        auth: {
            provider: {
                magicLink: {
                    sendMagicLink: (email: string) =>
                        client.post<MagicLinkRequestBody, AuthV1.ResponseAuthenticated>('/v1/auth/provider/magicLink/request', {
                            body: {
                                action: 'sendMagicLink',
                                email,
                                destination: 'admin',
                            }
                        }),
                    verifyMagicLink: (token: string) =>
                        client.post<MagicLinkRequestBody, AuthV1.ResponseAuthenticated>('/v1/auth/provider/magicLink/request', {
                            body: {
                                action: 'verifyMagicLink',
                                token
                            },
                            query: {
                                useCookieRefreshToken: true
                            },
                            transformResponse: (res) => ({
                                ...res,
                                tokens: {
                                    ...res.tokens,
                                    accessTokenExpiresAt: new Date(res.tokens.accessTokenExpiresAt)
                                }
                            })
                        }),
                }
            }
        },
        config: {
            getFrontend: () =>
                client.get<ConfigV1.FrontendConfig>('/v1/config/frontend', {}),
        },
        identities: {
            getMyIdentity: () =>
                client.get<IdentityV1.OwnRecord>('/v1/identities/me', {}),
            getAll: (query?: Record<string, unknown>) =>
                client.get<IdentityV1.List>('/v1/identities/all', { query }),
            getById: (identityId: string) =>
                client.get<IdentityV1.Record>('/v1/identities/all/id/:identityId', {
                    params: { identityId }
                }),
            update: (identityId: string, data: IdentityV1.Update) =>
                client.put<IdentityV1.Update, IdentityV1.Record>('/v1/identities/all/id/:identityId', {
                    params: { identityId },
                    body: data
                }),
        },
        documents: {
            getAll: (query?: Record<string, unknown>) =>
                client.get<DocumentV1.List>('/v1/documents/all', { query }),
            getInvoiceUrl: (documentId: string) =>
                client.get<PaymentV1.GetInvoiceUrlResponse>('/v1/documents/id/:documentId/invoiceUrl', {
                    params: { documentId }
                }),
            markAsPaid: (documentId: string) =>
                client.post<null, void>('/v1/documents/id/:documentId/markAsPaid', {
                    params: { documentId },
                    body: null
                }),
            markAsUnpaid: (documentId: string) =>
                client.post<null, void>('/v1/documents/id/:documentId/markAsUnpaid', {
                    params: { documentId },
                    body: null
                }),
        },
        templates: {
            getAll: (query?: Record<string, unknown>) =>
                client.get<DocTemplateV1.List>('/v1/templates', { query }),
            getById: (templateId: string) =>
                client.get<DocTemplateV1.Record>('/v1/templates/id/:templateId', {
                    params: { templateId }
                }),
            create: (data: DocTemplateV1.Create) =>
                client.post<DocTemplateV1.Create, DocTemplateV1.Record>('/v1/templates', {
                    body: data
                }),
            update: (templateId: string, data: DocTemplateV1.Update) =>
                client.put<DocTemplateV1.Update, DocTemplateV1.Record>('/v1/templates/id/:templateId', {
                    params: { templateId },
                    body: data
                }),
            delete: (templateId: string) =>
                client.delete<void>('/v1/templates/id/:templateId', {
                    params: { templateId }
                }),
            duplicate: (templateId: string) =>
                client.post<Record<string, never>, DocTemplateV1.Record>('/v1/templates/id/:templateId/duplicate', {
                    params: { templateId },
                    body: {}
                }),
            getUploadLink: (data: DocTemplateV1.UploadLinkRequest) =>
                client.post<DocTemplateV1.UploadLinkRequest, DocTemplateV1.UploadLinkResponse>('/v1/templates/upload-url', {
                    body: data
                }),
        },
        aiFeaturePrices: {
            getAll: () =>
                client.get<CreditV1.AiFeaturePriceList>('/v1/credits/feature-prices/all', {}),
            getByType: (featureType: string) =>
                client.get<CreditV1.AiFeaturePrice>('/v1/credits/feature-prices/:featureType', {
                    params: { featureType }
                }),
            bulkUpdate: (data: CreditV1.AiFeaturePriceBulkUpdate) =>
                client.put<CreditV1.AiFeaturePriceBulkUpdate, CreditV1.AiFeaturePriceList>('/v1/credits/feature-prices/update-all', {
                    body: data
                }),
        },
        vouchers: {
            getAll: () =>
                client.get<VoucherV1.List>('/v1/vouchers/all', {}),
            getById: (voucherId: string) =>
                client.get<VoucherV1.Record>('/v1/vouchers/:voucherId', {
                    params: { voucherId }
                }),
            create: (data: VoucherV1.Create) =>
                client.post<VoucherV1.Create, VoucherV1.Record>('/v1/vouchers/create', {
                    body: data
                }),
            update: (voucherId: string, data: VoucherV1.Update) =>
                client.put<VoucherV1.Update, VoucherV1.Record>('/v1/vouchers/update/:voucherId', {
                    params: { voucherId },
                    body: data
                }),
            delete: (voucherId: string) =>
                client.delete<void>('/v1/vouchers/delete/:voucherId', {
                    params: { voucherId }
                }),
        },
        paymentOrders: {
            getAll: () =>
                client.get<PaymentV1.PaymentOrderList>('/v1/paymentOrders/all', {}),
            getById: (orderId: string) =>
                client.get<PaymentV1.PaymentOrderRecord>('/v1/paymentOrders/:orderId', {
                    params: { orderId }
                }),
            getInvoiceUrl: (orderId: string) =>
                client.get<PaymentV1.GetInvoiceUrlResponse>('/v1/paymentOrders/:orderId/invoiceUrl', {
                    params: { orderId }
                }),
        },
        jobQueue: {
            getAll: (query?: Record<string, unknown>) =>
                client.get<JobQueueV1.List>('/v1/job-queue', { query }),
            getById: (jobId: string) =>
                client.get<JobQueueV1.Record>('/v1/job-queue/job/id/:jobId', {
                    params: { jobId }
                }),
            cancel: (jobId: string) =>
                client.post<Record<string, never>, void>('/v1/job-queue/job/id/:jobId/cancel', {
                    params: { jobId },
                    body: {}
                }),
        },
        creditPackages: {
            getAll: () =>
                client.get<CreditV1.PackageList>('/v1/credits/packages/all', {}),
            getById: (packageId: string) =>
                client.get<CreditV1.Package>('/v1/credits/packages/:packageId', {
                    params: { packageId }
                }),
            create: (data: CreditV1.PackageCreate) =>
                client.post<CreditV1.PackageCreate, CreditV1.Package>('/v1/credits/packages/create', {
                    body: data
                }),
            update: (packageId: string, data: CreditV1.PackageUpdate) =>
                client.put<CreditV1.PackageUpdate, CreditV1.Package>('/v1/credits/packages/update/:packageId', {
                    params: { packageId },
                    body: data
                }),
            delete: (packageId: string) =>
                client.delete<void>('/v1/credits/packages/delete/:packageId', {
                    params: { packageId }
                }),
        },
        usageLogs: {
            getAll: (query?: { limit?: number; offset?: number }) =>
                client.get<UsageLogV1.List>('/v1/usage-logs/all', { query }),
        },
        documentUnlockSettings: {
            get: () =>
                client.get<DocumentUnlockSettingsV1.Record>('/v1/document-unlock-settings/', {}),
            update: (data: DocumentUnlockSettingsV1.Update) =>
                client.put<DocumentUnlockSettingsV1.Update, DocumentUnlockSettingsV1.Record>('/v1/document-unlock-settings/', { body: data }),
        },
    },
});

export type AdminApiEndpoints = ReturnType<typeof createAdminApi>;
